import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isEqual } from 'lodash';
import mediaQueries from 'app/utils/mediaQueries';
import { getElementPosition } from 'app/utils/helpers';
import mkLazyImage from 'app/utils/lazyImage';
import { displayFooter } from 'app/utils/globalDataHelpers';
import dlTrackMediaBannersEvents from 'app/analytics/mediaBannersEventsAnalytics';
import {
  MINI_WISHLIST_ROOT_ELEMENT,
  TRANSFER_MINI_WISHLIST_ADD,
  TRANSFER_MINI_WISHLIST_REMOVE,
} from 'app/header/miniWishlist/miniWishlistConstants';
import OutOfStockNotificationModal from 'app/commonComponents/outOfStockNotificationModal/outOfStockNotificationModal';
import { FormattedMessage } from 'react-intl';
import BackToTop from '../backToTop/scroll';
import LoadingIndicator from './loadingIndicator';
import TemplateSwicher from './templateSwitcher/templateSwitcher';
import {
  gridColumnsToShow,
  addContentSpotsToProductsGrid,
  addProductsToProductGrid,
  customizeContentSpots,
  handleInfiniteScrollObserver,
  checkScrollSpeed,
  takeProducts,
} from './productListUtils';
import './product-list.scss';

export const PRODUCTS_LOAD_LIMIT = 24;

const {
  advancedCommerce: { enabled: isListingPage },
} = window.inlineGlobalConfig;

class ProductList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      productsRefs: [],
      previousProductsRefs: [],
      productsGrid: {},
      isGridView: props.isGridView,
      columnsToShow: gridColumnsToShow(props.isGridView),
      contentSpots: window.productContentSpots || [],
      contentSpotsDomElements: {},
      contentSpotRefs: [],
      lastProducts: props.products,
      isNotificationModalOpen: false,
      onBackBtnClick: () => {},
      productNotificationData: {
        selectedSize: {},
        hasStock: true,
        sizes: [],
        details: {},
        heroImage: '',
        name: '',
        colourName: '',
        description: '',
        price: '',
        productType: '',
      },
      productsToShow: PRODUCTS_LOAD_LIMIT,
    };

    this.onResize = this.onResize.bind(this);
    this.customizeContentSpots = this.customizeContentSpots.bind(this);
    this.horizontalFilterSticky = this.horizontalFilterSticky.bind(this);
    this.addContentSpotsDomRefs = this.addContentSpotsDomRefs.bind(this);
    this.updateContentSpotRefs = this.updateContentSpotRefs.bind(this);
    this.addProductsReferences = this.addProductsReferences.bind(this);
    this.removeProductsReferences = this.removeProductsReferences.bind(this);
    this.lastProductLoaded = this.lastProductLoaded.bind(this);
    this.keepScrollPositionOnProductsPrepend = this.keepScrollPositionOnProductsPrepend.bind(this);
    this.closeNotificationModal = this.closeNotificationModal.bind(this);
    this.openNotificationModal = this.openNotificationModal.bind(this);
    this.loadMoreProducts = this.loadMoreProducts.bind(this);
    this.showAllProducts = this.showAllProducts.bind(this);
  }

  loadMoreProducts() {
    if (isListingPage) {
      this.props.fetchAdditionalProducts();
    } else {
      // This is the original logic that is still needed
      this.setState(prevState => {
        const remainingProducts = prevState.lastProducts.length - prevState.productsToShow;
        const productsToLoad = remainingProducts >= PRODUCTS_LOAD_LIMIT ? PRODUCTS_LOAD_LIMIT : remainingProducts;
        const productsToShow = prevState.productsToShow + productsToLoad;
        this.props.updateDisplayedItems(productsToShow);

        return {
          productsToShow,
        };
      });
    }
  }

  showAllProducts() {
    if (isListingPage) {
      this.props.fetchAllProducts();
    } else {
      this.setState(() => {
        const productsToShow = this.props.numberOfProducts;
        this.props.updateDisplayedItems(productsToShow);

        return {
          productsToShow,
        };
      });
    }
  }

  componentDidMount() {
    const { updateInWishlistProducts } = this.props;

    this.lazyImage = mkLazyImage();
    this.addContentSpotsDomRefs();

    window.addEventListener('resize', this.onResize);
    window.addEventListener('resize', this.customizeContentSpots);

    if (mediaQueries.is_large_up()) {
      window.addEventListener('scroll', this.horizontalFilterSticky);
    }

    [TRANSFER_MINI_WISHLIST_ADD, TRANSFER_MINI_WISHLIST_REMOVE].forEach(event => {
      MINI_WISHLIST_ROOT_ELEMENT.addEventListener(event, e => {
        updateInWishlistProducts(e.detail);
      });
    });
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (!isEqual(prevState.lastProducts, nextProps.products)) {
      const { contentSpots } = prevState;
      const allProducts = [].concat(nextProps.products);
      const productsGrid = {
        1: [],
        2: [],
        3: [],
        4: [],
      };

      const productsGridWithContentSpots = addContentSpotsToProductsGrid(contentSpots, productsGrid);
      const productsGridWithProducts = addProductsToProductGrid(allProducts, productsGridWithContentSpots);
      return {
        lastProducts: nextProps.products,
        productsGrid: productsGridWithProducts,
      };
    }

    if (prevState.isGridView !== nextProps.isGridView) {
      const columnsToShow = gridColumnsToShow(nextProps.isGridView);
      return {
        isGridView: nextProps.isGridView,
        columnsToShow: prevState.columnsToShow !== columnsToShow ? columnsToShow : prevState.columnsToShow,
      };
    }

    return null;
  }

  componentDidUpdate(prevProps) {
    const { productsRefs, previousProductsRefs, contentSpotRefs } = this.state;
    const { isFetching, onInfiniteScrollIntersection, numberOfProducts, products } = this.props;

    if (isListingPage && prevProps.products.length !== this.props.products.length) {
      this.setState({ productsToShow: products.length });
    }

    const stateUpdates = {};

    if (numberOfProducts !== prevProps.numberOfProducts) {
      // eslint-disable-next-line
      stateUpdates['productsToShow'] = PRODUCTS_LOAD_LIMIT;
    }

    if (prevProps.numberOfProducts === 0 && numberOfProducts) {
      dlTrackMediaBannersEvents();
    }

    if (!isEmpty(contentSpotRefs)) {
      this.customizeContentSpots();
    }

    if (productsRefs.length && !isEqual(productsRefs, previousProductsRefs)) {
      // eslint-disable-next-line
      stateUpdates['previousProductsRefs'] = [].concat(productsRefs);

      if (!isFetching && !isListingPage) {
        handleInfiniteScrollObserver(productsRefs, onInfiniteScrollIntersection);
      }
    }

    if (Object.keys(stateUpdates).length) {
      this.setState(stateUpdates);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('resize', this.customizeContentSpots);
  }

  onResize() {
    const { columnsToShow } = this.state;
    const { isGridView } = this.props;
    const getColumnsToShow = gridColumnsToShow(isGridView);
    if (columnsToShow !== getColumnsToShow) {
      this.setState({
        columnsToShow: getColumnsToShow,
      });
    }

    if (mediaQueries.is_large_up()) {
      window.addEventListener('scroll', this.horizontalFilterSticky);
    } else {
      window.removeEventListener('scroll', this.horizontalFilterSticky);
    }
  }

  horizontalFilterSticky() {
    const html = document.querySelector('html');
    const plpPosition = getElementPosition(document.getElementById('plp'));
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    if (scrollTop < plpPosition) {
      html.classList.remove('is-scrolling-down');
      html.classList.remove('is-scrolling-up');
      return;
    }

    const isFastEnough = 30;
    const scrollSpeed = checkScrollSpeed();
    if (this.oldScroll > scrollTop && scrollSpeed > isFastEnough) {
      html.classList.add('is-scrolling-up');
      html.classList.remove('is-scrolling-down');
    }

    if (this.oldScroll < scrollTop) {
      html.classList.add('is-scrolling-down');
      html.classList.remove('is-scrolling-up');
    }

    this.oldScroll = scrollTop;
  }

  customizeContentSpots() {
    const { contentSpotRefs } = this.state;
    customizeContentSpots(contentSpotRefs);
  }

  addProductsReferences(productRef) {
    this.setState(prevState => ({
      ...prevState,
      productsRefs: prevState.productsRefs.concat([productRef]),
    }));
  }

  removeProductsReferences(productRef) {
    this.setState(prevState => ({
      ...prevState,
      productsRefs: prevState.productsRefs.slice(0).filter(ref => ref !== productRef),
    }));
  }

  addContentSpotsDomRefs() {
    const { contentSpots } = this.state;
    if (contentSpots.length) {
      this.setState(
        {
          contentSpotsDomElements: contentSpots.reduce((acc, curr) => {
            [...document.getElementById(curr.id).querySelectorAll('[data-analytics-attached]')].forEach(element => {
              element.removeAttribute('data-analytics-attached');
            });
            return {
              ...acc,
              [curr.id]: document.getElementById(curr.id).innerHTML,
            };
          }, {}),
        },
        () => {
          const contentSpotsParent = document.getElementById('content-spots');
          contentSpotsParent.parentNode.removeChild(contentSpotsParent);
        }
      );
    }
  }

  updateContentSpotRefs(contentSpotRef) {
    const { contentSpotRefs } = this.state;

    if (contentSpotRef && !contentSpotRefs.includes(contentSpotRef)) {
      this.setState(prevState => ({
        ...prevState,
        contentSpotRefs: prevState.contentSpotRefs.concat(contentSpotRef),
      }));
    }
  }

  // This is mainly used for scenarios after clicking VIEW PREVIOUS button
  // to keep the viewport in same position instead jumping to top;
  lastProductLoaded() {
    this.keepScrollPositionOnProductsPrepend();
    displayFooter();
  }

  keepScrollPositionOnProductsPrepend(docHeightOnClick) {
    if (docHeightOnClick) {
      this.docHeightBeforePrepending = docHeightOnClick;
      return;
    }
    if (this.docHeightBeforePrepending) {
      const currentDocHeight = document.querySelector('html').scrollHeight;

      window.scrollTo(0, currentDocHeight - this.docHeightBeforePrepending + window.pageYOffset);
      this.docHeightBeforePrepending = null;
    }
  }

  closeNotificationModal() {
    this.setState(prevState => ({
      ...prevState,
      isNotificationModalOpen: false,
    }));
  }

  openNotificationModal(productNotificationData, onBackBtnClick) {
    this.setState(prevState => ({
      ...prevState,
      productNotificationData,
      isNotificationModalOpen: true,
      onBackBtnClick,
    }));
  }

  render() {
    const {
      productsGrid,
      columnsToShow,
      lastProducts,
      contentSpotsDomElements,
      isNotificationModalOpen,
      productNotificationData,
      onBackBtnClick,
      productsToShow,
    } = this.state;
    const {
      products,
      numberOfProducts,
      showLoader,
      isGridView,
      updateProductDetails,
      onSizeSelected,
      onSizeDeselected,
      onAddToWishlist,
      referrerProductId,
      cleanReferrerProductId,
      error,
    } = this.props;

    const shouldShowPagination = isListingPage
      ? products.length < numberOfProducts
      : productsToShow < lastProducts.length;

    if (numberOfProducts === 0 || !productsGrid[columnsToShow]) {
      if (error) {
        return (
          <div style={{ padding: '40px 0' }}>
            <h2 style={{ textAlign: 'center' }}>
              <FormattedMessage id="product.plp.notLoadedPage" description="not loaded plp" />
            </h2>
          </div>
        );
      }
      return null;
    }

    this.lazyImage.setItemsPerRow(columnsToShow);

    const remainingProducts = isListingPage ? numberOfProducts - products.length : lastProducts.length - productsToShow;

    const productsShownCount = isListingPage ? products.length : Math.min(productsToShow, lastProducts.length);

    const loadMoreText =
      remainingProducts <= PRODUCTS_LOAD_LIMIT ? (
        <FormattedMessage
          id="products.productgrid.showMore"
          values={{ 0: remainingProducts }}
          description="Show more"
        />
      ) : (
        <FormattedMessage
          id="products.productgrid.showMore"
          values={{ 0: PRODUCTS_LOAD_LIMIT }}
          description="Show more"
        />
      );

    const productsToDisplay = takeProducts(productsGrid, columnsToShow, productsToShow);

    return (
      <div className="product-list__container">
        <LoadingIndicator showLoader={showLoader} />
        <div className={`product-list__container-grid ${showLoader ? 'is-fetching' : ''}`}>
          {productsToDisplay.map(row => (
            <TemplateSwicher
              {...row}
              key={`templateSwicher_${row.row}`}
              LazyImageComponent={this.lazyImage.Component}
              columnsToShow={columnsToShow}
              contentSpotsDomElements={contentSpotsDomElements}
              isGridView={isGridView}
              lastProductLoaded={this.lastProductLoaded}
              onAddToWishlist={onAddToWishlist}
              onSizeDeselected={onSizeDeselected}
              onSizeSelected={onSizeSelected}
              referrerProductId={referrerProductId}
              cleanReferrerProductId={cleanReferrerProductId}
              totalFetchedProducts={lastProducts.length}
              updateContentSpotRefs={this.updateContentSpotRefs}
              updateProductDetails={updateProductDetails}
              addProductsReferences={this.addProductsReferences}
              removeProductsReferences={this.removeProductsReferences}
              openNotificationModal={this.openNotificationModal}
              isNotificationModalOpen={isNotificationModalOpen}
            />
          ))}
        </div>
        <div className="product-list__load-more">
          <p>
            <FormattedMessage id="products.productgrid.showing" /> <b>{productsShownCount}</b>{' '}
            <FormattedMessage id="products.productgrid.showing.of" /> <b>{numberOfProducts}</b>
          </p>
          {shouldShowPagination && (
            <>
              <button type="button" onClick={this.loadMoreProducts} className="load-more-button">
                {loadMoreText}
              </button>
              <button type="button" onClick={this.showAllProducts} className="show-all-button">
                <FormattedMessage id="products.productgrid.showAll" />
              </button>
            </>
          )}
        </div>
        <OutOfStockNotificationModal
          isModalOpen={isNotificationModalOpen}
          toggleModal={() => {
            this.closeNotificationModal();
            onBackBtnClick();
          }}
          productDetails={productNotificationData}
        />
        <BackToTop />
      </div>
    );
  }
}

ProductList.propTypes = {
  showLoader: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  isGridView: PropTypes.bool.isRequired,
  numberOfProducts: PropTypes.number.isRequired,
  onInfiniteScrollIntersection: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  referrerProductId: PropTypes.string.isRequired,
  cleanReferrerProductId: PropTypes.func.isRequired,
  updateInWishlistProducts: PropTypes.func.isRequired,
  updateDisplayedItems: PropTypes.func.isRequired,
};

export default ProductList;
